import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth, useToast } from '../contexts/AppContexts';
import { compressImage } from '../utils/imageCompression';
import useImageDrop from '../utils/useImageDrop';
import { Camera, Edit, Image as ImageIcon, Monitor, Trash, Upload, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${API_BASE_URL}/api`;

const ImageRepository = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const { token, user } = useAuth();
  const toast = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null); // holds editable fields during edit
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  
  // Upload form state
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    date_time: new Date().toISOString().slice(0, 16),
    location: '',
    category: 'Prevention',
    image_type: 'Exercise',
    image_data: '',
    content_type: ''
  });

  const categories = ['Prevention', 'Mitigation', 'Preparedness', 'Response', 'Recovery'];
  const imageTypes = ['Exercise', 'Real', 'ICS'];
  
  const canDelete = ['Super Admin', 'Agency Admin'].includes(user?.role);

  const { dropProps: repoDropProps, isDragging: isRepoDragging } = useImageDrop({
    onFile: (file) => handleFileSelect({ target: { files: [file] } })
  });

  useEffect(() => {
    fetchImages();
  }, [filterCategory, filterType, currentExercise?.id]);

  const fetchImages = async () => {
    try {
      let url = `${API}/images`;
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterType) params.append('image_type', filterType);
      // Filter by current exercise if available
      if (currentExercise?.id) params.append('exercise_id', currentExercise.id);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
    }
  };

  // Image compression utility for Image Repository
  // Uses larger dimensions (1920x1080) for exercise/scenario images
  const compressImage = (imageDataUrl, maxWidth = 1920, quality = 0.75) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        const maxHeight = 1080;
        
        // Scale down to fit within max dimensions
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale
        
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Use better quality settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed JPEG (targets max 800KB for exercise images)
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // Log compression results
        const originalSize = imageDataUrl.length * 0.75; // Approximate original size
        const newSize = compressedDataUrl.length * 0.75;
        console.log(`Image Repository: Compressed ${(originalSize/1024).toFixed(0)}KB → ${(newSize/1024).toFixed(0)}KB (${width}x${height})`);
        
        resolve(compressedDataUrl);
      };
      img.src = imageDataUrl;
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error('Invalid file type. Please upload JPEG, JPG, PNG, GIF, or HEIC files.');
      return;
    }

    // Validate file size (10MB max for original)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size too large. Maximum 10MB allowed.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Compress the image with optimal settings (2560px max width, 90% quality)
        // This targets 3-5MB final size for best quality and performance balance
        const compressedImage = await compressImage(reader.result, 2560, 0.90);
        
        setUploadData(prev => ({
          ...prev,
          image_data: compressedImage,
          content_type: 'image/jpeg', // Always JPEG after compression
          name: prev.name || file.name.split('.')[0]
        }));
        
        toast.success('Image optimized and ready for upload!');
      } catch (error) {
        console.error('Compression error:', error);
        toast.error('Error processing image');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      // Wait for video to be ready
      video.onloadedmetadata = async () => {
        // Create canvas and capture frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        // Convert to base64
        const imageData = canvas.toDataURL('image/jpeg', 0.9);
        
        // Stop camera
        stream.getTracks().forEach(track => track.stop());
        
        // Compress the captured image with optimal settings
        try {
          const compressedImage = await compressImage(imageData, 2560, 0.90);
          
          setUploadData(prev => ({
            ...prev,
            image_data: compressedImage,
            content_type: 'image/jpeg',
            name: prev.name || `Camera_${new Date().getTime()}`
          }));
          
          toast.success('Photo captured and compressed!');
        } catch (error) {
          console.error('Compression error:', error);
          toast.error('Error processing captured photo');
        }
      };
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const handleUpload = async () => {
    if (!uploadData.image_data) {
      toast.error('Please select or capture an image');
      return;
    }
    if (!uploadData.name.trim()) {
      toast.error('Please enter an image name');
      return;
    }

    setLoading(true);
    try {
      // Include exercise_id and exercise_name in upload data
      const uploadPayload = {
        ...uploadData,
        date_time: new Date(uploadData.date_time).toISOString(),
        exercise_id: currentExercise?.id || null,
        exercise_name: currentExercise?.name || null
      };
      
      await axios.post(
        `${API}/images`,
        uploadPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Image uploaded successfully${currentExercise ? ` to ${currentExercise.name}` : ''}!`);
      setShowUploadModal(false);
      setUploadData({
        name: '',
        description: '',
        date_time: new Date().toISOString().slice(0, 16),
        location: '',
        category: 'Prevention',
        image_type: 'Exercise',
        image_data: '',
        content_type: ''
      });
      fetchImages();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted successfully');
      fetchImages();
      if (selectedImage?.id === imageId) setSelectedImage(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image');
    }
  };

  const startEditing = (image) => {
    setEditingImage({
      name: image.name || '',
      description: image.description || '',
      date_time: image.date_time ? new Date(image.date_time).toISOString().slice(0, 16) : '',
      location: image.location || '',
      category: image.category || 'Prevention',
      image_type: image.image_type || 'Exercise'
    });
  };

  const handleUpdate = async () => {
    if (!selectedImage || !editingImage) return;
    setLoading(true);
    try {
      const payload = {
        ...editingImage,
        date_time: editingImage.date_time ? new Date(editingImage.date_time).toISOString() : undefined
      };
      const response = await axios.put(`${API}/images/${selectedImage.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image updated successfully');
      setSelectedImage(response.data);
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">Image Repository</h1>
          <p className="text-gray-400">
            {currentExercise 
              ? `Images for Exercise: ${currentExercise.name}` 
              : 'Capture and manage exercise, real event, and ICS response images'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => window.open('#display-images', '_blank')} 
            className="bg-purple-600 hover:bg-purple-700"
            title="Open in Display Mode"
          >
            <Monitor className="h-4 w-4 mr-2" />
            Display Mode
          </Button>
          {user?.role !== 'Viewer' && (
            <Button onClick={() => setShowUploadModal(true)} className="bg-orange-500 hover:bg-orange-600">
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label>Filter by Category</Label>
          <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
            <SelectTrigger className="bg-gray-800 border-gray-700">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Filter by Type</Label>
          <Select value={filterType || "all"} onValueChange={(val) => setFilterType(val === "all" ? "" : val)}>
            <SelectTrigger className="bg-gray-800 border-gray-700">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all">All Types</SelectItem>
              {imageTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map(image => (
          <Card key={image.id} className="bg-gray-800 border-gray-700 cursor-pointer hover:border-orange-500 transition-colors" onClick={() => setSelectedImage(image)}>
            <CardContent className="p-2">
              <img src={image.image_data} alt={image.name} className="w-full h-48 object-cover rounded mb-2" />
              <h3 className="text-white font-semibold truncate">{image.name}</h3>
              {image.exercise_name && (
                <p className="text-xs text-blue-400 truncate mt-1">📍 {image.exercise_name}</p>
              )}
              <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                <Badge className="text-xs">{image.category}</Badge>
                <Badge variant="outline" className="text-xs">{image.image_type}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-12">
          <ImageIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No images found. Upload your first image to get started!</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-orange-500">Upload Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                {...repoDropProps}
                data-testid="image-repository-dropzone"
                className={`grid grid-cols-2 gap-4 rounded-lg border-2 border-dashed p-2 transition-colors ${
                  isRepoDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-700'
                }`}
                title="Drop an image anywhere in this area to upload"
              >
                <Button onClick={() => document.getElementById('fileInput').click()} variant="outline" className="h-32">
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto mb-2" />
                    <p>Choose File</p>
                    <p className="text-xs text-gray-400 mt-1">or drop image here</p>
                  </div>
                </Button>
                <Button onClick={handleCameraCapture} variant="outline" className="h-32">
                  <div className="text-center">
                    <Camera className="h-8 w-8 mx-auto mb-2" />
                    <p>Take Photo</p>
                  </div>
                </Button>
                <input id="fileInput" type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/heic" onChange={handleFileSelect} className="hidden" />
              </div>

              {uploadData.image_data && (
                <div>
                  <Label>Preview</Label>
                  <img src={uploadData.image_data} alt="Preview" className="w-full h-48 object-contain bg-gray-800 rounded" />
                </div>
              )}

              <div>
                <Label>Name *</Label>
                <Input value={uploadData.name} onChange={(e) => setUploadData({...uploadData, name: e.target.value})} className="bg-gray-800 border-gray-700" />
              </div>

              <div>
                <Label>Description</Label>
                <textarea value={uploadData.description} onChange={(e) => setUploadData({...uploadData, description: e.target.value})} className="w-full bg-gray-800 border-gray-700 rounded p-2" rows="3" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={uploadData.date_time ? uploadData.date_time.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = uploadData.date_time ? uploadData.date_time.split('T')[1] : '00:00';
                        setUploadData({...uploadData, date_time: `${date}T${time}`});
                      }}
                      className="bg-gray-800 border-gray-700"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={uploadData.date_time ? uploadData.date_time.split('T')[1] : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = uploadData.date_time ? uploadData.date_time.split('T')[0] : '';
                          setUploadData({...uploadData, date_time: `${date}T${value}`});
                        }
                      }}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={uploadData.location} onChange={(e) => setUploadData({...uploadData, location: e.target.value})} className="bg-gray-800 border-gray-700" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={uploadData.category} onValueChange={(value) => setUploadData({...uploadData, category: value})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={uploadData.image_type} onValueChange={(value) => setUploadData({...uploadData, image_type: value})}>
                    <SelectTrigger className="bg-gray-800 border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {imageTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button onClick={() => setShowUploadModal(false)} variant="outline">Cancel</Button>
                <Button onClick={handleUpload} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
                  {loading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedImage(null); setEditingImage(null); }}>
          <Card className="bg-gray-900 border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-orange-500" data-testid="image-detail-title">
                  {editingImage ? 'Edit Image' : selectedImage.name}
                </CardTitle>
                <button onClick={() => { setSelectedImage(null); setEditingImage(null); }} data-testid="close-image-detail">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <img src={selectedImage.image_data} alt={selectedImage.name} className="w-full max-h-96 object-contain bg-gray-800 rounded" />
              
              {editingImage ? (
                /* ── EDIT MODE ── */
                <div className="space-y-4" data-testid="image-edit-form">
                  <div>
                    <Label>Name *</Label>
                    <Input data-testid="edit-image-name" value={editingImage.name} onChange={(e) => setEditingImage({...editingImage, name: e.target.value})} className="bg-gray-800 border-gray-700" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <textarea data-testid="edit-image-description" value={editingImage.description} onChange={(e) => setEditingImage({...editingImage, description: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white" rows="3" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date & Time</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          data-testid="edit-image-date"
                          type="date"
                          value={editingImage.date_time ? editingImage.date_time.split('T')[0] : ''}
                          onChange={(e) => {
                            const date = e.target.value;
                            const time = editingImage.date_time ? editingImage.date_time.split('T')[1] : '00:00';
                            setEditingImage({...editingImage, date_time: `${date}T${time}`});
                          }}
                          className="bg-gray-800 border-gray-700"
                        />
                        <Input
                          data-testid="edit-image-time"
                          type="text"
                          placeholder="HH:MM"
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          maxLength="5"
                          value={editingImage.date_time ? editingImage.date_time.split('T')[1] : ''}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9:]/g, '');
                            if (value.length === 2 && !value.includes(':')) value = value + ':';
                            if (value.length <= 5) {
                              const date = editingImage.date_time ? editingImage.date_time.split('T')[0] : '';
                              setEditingImage({...editingImage, date_time: `${date}T${value}`});
                            }
                          }}
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input data-testid="edit-image-location" value={editingImage.location} onChange={(e) => setEditingImage({...editingImage, location: e.target.value})} className="bg-gray-800 border-gray-700" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category *</Label>
                      <Select value={editingImage.category} onValueChange={(value) => setEditingImage({...editingImage, category: value})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700" data-testid="edit-image-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type *</Label>
                      <Select value={editingImage.image_type} onValueChange={(value) => setEditingImage({...editingImage, image_type: value})}>
                        <SelectTrigger className="bg-gray-800 border-gray-700" data-testid="edit-image-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          {imageTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => setEditingImage(null)} data-testid="cancel-edit-image">Cancel</Button>
                    <Button onClick={handleUpdate} disabled={loading || !editingImage.name.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="save-edit-image">
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                /* ── VIEW MODE ── */
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>Category:</strong> <Badge>{selectedImage.category}</Badge></div>
                    <div><strong>Type:</strong> <Badge variant="outline">{selectedImage.image_type}</Badge></div>
                    <div><strong>Date:</strong> {new Date(selectedImage.date_time).toLocaleString()}</div>
                    <div><strong>Location:</strong> {selectedImage.location || 'Not specified'}</div>
                    <div><strong>Uploaded by:</strong> {selectedImage.uploader_name}</div>
                    <div><strong>Uploaded:</strong> {new Date(selectedImage.created_at).toLocaleDateString()}</div>
                  </div>

                  {selectedImage.description && (
                    <div>
                      <strong>Description:</strong>
                      <p className="text-gray-400 mt-1">{selectedImage.description}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    {user?.role !== 'Viewer' && (
                      <Button onClick={() => startEditing(selectedImage)} variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10" data-testid="edit-image-btn">
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {canDelete && (
                      <Button onClick={() => handleDelete(selectedImage.id)} variant="destructive" data-testid="delete-image-btn">
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Time Unit Collection Form

export default ImageRepository;
